import React, { useState, useEffect } from 'react';
import client from '../api/client';

/**
 * Displays past LP tracking runs in a table with pagination.
 * Allows viewing details and editing ISK/LP rate inline.
 */
function LPRunHistoryTable({ onViewDetails, refreshTrigger }) {
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRuns, setTotalRuns] = useState(0);
    const [editingIskPerLp, setEditingIskPerLp] = useState({});

    useEffect(() => {
        fetchRuns();
    }, [page, refreshTrigger]);

    const fetchRuns = async () => {
        try {
            setLoading(true);
            const response = await client.get(`/lp/tracking/runs/?page=${page}&per_page=10`);
            setRuns(response.data.runs || []);
            setTotalPages(response.data.total_pages || 1);
            setTotalRuns(response.data.total_runs || 0);
            setError(null);
        } catch (err) {
            console.error('Error fetching runs:', err);
            setError('Failed to load run history');
        } finally {
            setLoading(false);
        }
    };

    const updateIskPerLp = async (runId, newValue) => {
        try {
            await client.patch(`/lp/tracking/runs/${runId}/`, {
                isk_per_lp: parseFloat(newValue)
            });

            // Update local state
            setRuns(prev =>
                prev.map(run =>
                    run.id === runId
                        ? {
                            ...run,
                            isk_per_lp: parseFloat(newValue),
                            // Recalculate metrics
                            lp_isk: run.lp_gained * parseFloat(newValue),
                            total_isk: (run.lp_gained * parseFloat(newValue)) + run.wallet_diff,
                            isk_per_hour: ((run.lp_gained * parseFloat(newValue)) + run.wallet_diff) / run.duration_hours,
                            isk_per_hour_per_char: (((run.lp_gained * parseFloat(newValue)) + run.wallet_diff) / run.duration_hours) / run.character_count
                        }
                        : run
                )
            );

            setEditingIskPerLp(prev => ({ ...prev, [runId]: false }));
        } catch (err) {
            console.error('Error updating ISK/LP:', err);
            alert('Failed to update ISK/LP rate');
        }
    };

    const formatDate = (isoString) => {
        const date = new Date(isoString);
        return date.toLocaleString();
    };

    const formatDuration = (hours) => {
        const h = Math.floor(hours);
        const m = Math.floor((hours - h) * 60);
        return `${h}h ${m}m`;
    };

    if (loading && runs.length === 0) {
        return <div className="table-empty">Loading runs...</div>;
    }

    if (error) {
        return <div className="table-empty text-neg">{error}</div>;
    }

    if (runs.length === 0) {
        return <div className="table-empty">No completed runs yet. Finished runs appear here.</div>;
    }

    return (
        <>
            <div className="table-scroll">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Duration</th>
                            <th>Characters</th>
                            <th className="num">LP gained</th>
                            <th className="num">ISK/LP</th>
                            <th className="num">Total ISK</th>
                            <th className="num">ISK/hour</th>
                            <th className="num">ISK/hour/char</th>
                            <th aria-label="Actions"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {runs.map(run => (
                            <tr key={run.id}>
                                <td>{formatDate(run.started_at)}</td>
                                <td>{formatDuration(run.duration_hours)}</td>
                                <td>
                                    <span className="lp-run-portraits">
                                        {run.characters && run.characters.slice(0, 3).map(char => (
                                            <img key={char.character_id} src={char.portrait_url} alt={char.character_name} title={char.character_name} />
                                        ))}
                                        {run.character_count > 3 && <span>+{run.character_count - 3}</span>}
                                    </span>
                                </td>
                                <td className="num text-pos">
                                    {run.lp_gained.toLocaleString()}
                                    {run.excluded_corp_count > 0 && (
                                        <span
                                            className="pill pill-warn lp-excluded"
                                            title={`${run.excluded_corp_count} corporation${run.excluded_corp_count > 1 ? 's' : ''} excluded from calculations`}
                                        >
                                            {run.excluded_corp_count} excluded
                                        </span>
                                    )}
                                </td>
                                <td className="num">
                                    {editingIskPerLp[run.id] ? (
                                        <input
                                            type="number"
                                            className="lp-inline-input"
                                            defaultValue={run.isk_per_lp}
                                            onBlur={(e) => {
                                                if (e.target.value && parseFloat(e.target.value) !== run.isk_per_lp) {
                                                    updateIskPerLp(run.id, e.target.value);
                                                } else {
                                                    setEditingIskPerLp(prev => ({ ...prev, [run.id]: false }));
                                                }
                                            }}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.target.blur();
                                                }
                                            }}
                                            autoFocus
                                            aria-label="ISK per LP"
                                        />
                                    ) : (
                                        <button
                                            className="lp-editable"
                                            onClick={() => setEditingIskPerLp(prev => ({ ...prev, [run.id]: true }))}
                                            title="Edit ISK per LP"
                                        >
                                            {run.isk_per_lp.toLocaleString()}
                                        </button>
                                    )}
                                </td>
                                <td className="num"><b>{run.total_isk.toLocaleString()}</b></td>
                                <td className="num">{run.isk_per_hour.toLocaleString()}</td>
                                <td className="num text-dim">{run.isk_per_hour_per_char.toLocaleString()}</td>
                                <td className="num">
                                    <button onClick={() => onViewDetails && onViewDetails(run.id)} className="btn btn-sm">
                                        Details
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="pager">
                <span>{totalRuns} run{totalRuns === 1 ? '' : 's'}</span>
                {totalPages > 1 && (
                    <span className="toolbar-end">
                        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1 || loading} className="btn btn-sm">
                            Previous
                        </button>
                        <span>Page {page} of {totalPages}</span>
                        <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages || loading} className="btn btn-sm">
                            Next
                        </button>
                    </span>
                )}
            </div>
        </>
    );
}

export default LPRunHistoryTable;
